/**
 * Zod-схемы модуля `support` — обращения в поддержку и переписка по ним.
 *
 * Зачем модуль вообще существует: пароля в IEC ID нет (Д1), поэтому сбой
 * доставки кода, потерянный доступ к ящику или конфликт идентичностей
 * оставляют человека ПОЛНОСТЬЮ снаружи экосистемы. До этого модуля
 * единственным ответом был почтовый адрес строкой на экране.
 *
 * Форма доступна и без сессии, и с ней. Отдельной «защищённой» копии процедур
 * нет намеренно: это была бы вторая дверь к тем же данным (см. запрет на
 * отдельную форму входа для операторов в CLAUDE.md).
 */
import { z } from "zod";
import {
	EmailSchema,
	IsoDateTimeSchema,
	LocaleSchema,
	paginated,
	ServiceIdSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";

// ─────────────────────────────── Темы ───────────────────────────────

/**
 * Темы обращения. TEXT в БД, а не enum: новая тема не должна требовать
 * миграции. Значения — одновременно ключи i18n (`topic.<value>`).
 *
 * Список общий, а поверхности показывают своё подмножество: на экранах входа
 * человек по определению не может спросить про списание Баллов, а в витрине
 * бессмысленно предлагать «не приходит код» — он уже вошёл.
 */
export const SUPPORT_TOPICS = [
	// Вход и доступ — видны и на входе, и в витрине.
	"sign_in_code_not_arriving",
	"sign_in_no_email_access",
	"sign_in_google",
	"sign_in_merge_accounts",
	"sign_in_legacy_account",
	"sign_in_account_blocked",
	"sign_in_error",
	// Профиль и данные.
	"account_email_change",
	"account_profile_data",
	"account_privacy_export_deletion",
	"account_legal_documents",
	// Продукт.
	"points_balance",
	"referrals",
	"subscription",
	"payment_refund",
	"plus_gift",
	"services",
	"notifications",
	// Всегда последняя.
	"other",
] as const;

export const SupportTopicSchema = z.enum(SUPPORT_TOPICS);

/** Что показываем на экранах входа: только то, о чём можно спросить снаружи. */
export const SIGNIN_SUPPORT_TOPICS = [
	"sign_in_code_not_arriving",
	"sign_in_no_email_access",
	"sign_in_google",
	"sign_in_merge_accounts",
	"sign_in_legacy_account",
	"sign_in_account_blocked",
	"sign_in_error",
	"other",
] as const satisfies readonly SupportTopic[];

/**
 * Что показываем в витрине — весь функционал системы.
 *
 * Темы «оспорить решение античита» здесь нет намеренно: апелляция уже
 * реализована отдельным потоком (`id-web/components/points/appeal-dialog.tsx`)
 * и ведёт к решению, а не к переписке. Дублировать её темой обращения значит
 * увести человека в канал, где его апелляция не будет засчитана.
 */
export const ACCOUNT_SUPPORT_TOPICS = [
	"sign_in_code_not_arriving",
	"sign_in_no_email_access",
	"account_email_change",
	"points_balance",
	"referrals",
	"subscription",
	"payment_refund",
	"plus_gift",
	"services",
	"notifications",
	"account_privacy_export_deletion",
	"account_legal_documents",
	"account_profile_data",
	"other",
] as const satisfies readonly SupportTopic[];

// ───────────────────────────── Вложения ─────────────────────────────

/**
 * Допустимые типы вложений — изображения и видео.
 *
 * Список закрытый и проверяется на сервере ПОСЛЕ загрузки (`storage.stat`):
 * presigned PUT не умеет выражать ни лимит размера, ни ограничение типа, и
 * то же самое уже описано для аватаров в `profile/_internal/avatar.ts`.
 * Файлы отдаются только по подписанной ссылке с `Content-Disposition:
 * attachment` и никогда не рендерятся в браузере.
 */
export const SUPPORT_ATTACHMENT_TYPES = [
	"image/jpeg",
	"image/png",
	"image/webp",
	"image/gif",
	"image/heic",
	"image/heif",
	"video/mp4",
	"video/webm",
	"video/quicktime",
] as const;

export const SupportAttachmentTypeSchema = z.enum(SUPPORT_ATTACHMENT_TYPES);

/** Не больше пяти файлов на обращение. */
export const MAX_SUPPORT_ATTACHMENTS = 5;

/** Суммарный потолок вложений одного обращения — 100 МБ. */
export const MAX_SUPPORT_ATTACHMENTS_TOTAL_BYTES = 100 * 1024 * 1024;

/** Presigned PUT живёт 15 минут: этого хватает на 100 МБ и на медленный канал. */
export const SUPPORT_UPLOAD_TTL_SEC = 15 * 60;

/**
 * Ссылка на вложение в письме поддержки живёт 30 дней.
 *
 * Прямой presigned URL сюда не годится: SigV4 живёт максимум 7 дней, а письмо
 * в ящике поддержки — сильно дольше. Поэтому в письме наша подписанная ссылка,
 * которая уже на месте меняется на свежий presign.
 */
export const SUPPORT_ATTACHMENT_LINK_TTL_SEC = 30 * 24 * 60 * 60;

/** Черновик без отправки живёт сутки, потом его и его файлы уносит cleanup. */
export const SUPPORT_DRAFT_TTL_HOURS = 24;

/** Тикет живёт 90 дней от последней активности (rules/compliance.md §9). */
export const SUPPORT_TICKET_TTL_DAYS = 90;

export const SupportAttachmentSchema = z.object({
	id: UuidV7Schema,
	fileName: z.string().min(1).max(255),
	contentType: SupportAttachmentTypeSchema,
	sizeBytes: z.number().int().positive(),
	/**
	 * Подписанная ссылка на скачивание — её собирает СЕРВЕР.
	 *
	 * Клиент не может построить её сам: подпись HMAC считается секретом,
	 * которого у браузера нет и быть не должно. Поэтому и в письме поддержки,
	 * и в карточке админки стоит один и тот же готовый адрес.
	 */
	url: z.url(),
});

// ─────────────────────────── Тикет и переписка ───────────────────────────

/**
 * Код тикета — то, что человек видит в письме и называет в разговоре.
 * Формат `IEC-XXXX-XX`, алфавит без похожих символов (0/O, 1/I).
 */
export const SupportTicketCodeSchema = z
	.string()
	.regex(/^IEC-[2-9A-HJ-NP-Z]{4}-[2-9A-HJ-NP-Z]{2}$/, "Код обращения — формат IEC-XXXX-XX");

export const SupportTicketStatusSchema = z.enum(["new", "in_progress", "answered", "closed"]);

/**
 * Кто написал сообщение и каким путём.
 *
 * `source: "email"` пока не появляется ни у одного сообщения: разбор входящей
 * почты (§7 плана) ждёт доступа к ящику. Поле заведено сразу, потому что
 * дописывать его миграцией в живую таблицу с перепиской дороже, чем оставить
 * незаполненным.
 */
export const SupportMessageAuthorSchema = z.enum(["user", "operator"]);
export const SupportMessageSourceSchema = z.enum(["web", "email"]);

/**
 * Нижняя граница текста обращения.
 *
 * Десять символов, а не двадцать: порог существует против пустой отправки и
 * ботов, а не против краткости. «Не приходит код» — законное обращение из
 * шестнадцати символов, и требовать от человека добить его до двадцати значит
 * мешать ровно тому, кто и так в затруднении.
 */
export const MIN_SUPPORT_MESSAGE_LENGTH = 10;

export const SupportMessageBodySchema = z.string().trim().min(MIN_SUPPORT_MESSAGE_LENGTH).max(4000);

/**
 * Сколько времени ответ оператора считается «в очереди», а не «недоставленным».
 *
 * Письмо уходит не в мутации, а Inngest-функцией через outbox, поэтому сразу
 * после нажатия «Отправить» строка законно лежит с `delivered_at IS NULL`.
 * Красная плашка «не доставлен» в эту секунду — ложь: письмо в пути, и
 * оператор, поверивший ей, отправляет второе.
 *
 * По истечении грации молчание перестаёт быть нормой: доставка либо
 * подтвердилась, либо оставила ошибку. Строка без того и другого означает, что
 * до неё не дошёл ни обработчик, ни свипер, — и это ровно тот случай, который
 * инвариант №14 требует показывать громко.
 */
export const SUPPORT_DELIVERY_GRACE_MS = 15 * 60 * 1000;

export const SupportMessageSchema = z.object({
	id: UuidV7Schema,
	author: SupportMessageAuthorSchema,
	source: SupportMessageSourceSchema,
	/** Имя оператора для карточки в админке; у сообщения человека — `null`. */
	operatorName: z.string().min(1).max(120).nullable(),
	body: z.string(),
	createdAt: IsoDateTimeSchema,
	/** Когда письмо реально ушло. `null` у неотправленного — плашка в админке. */
	deliveredAt: IsoDateTimeSchema.nullable(),
	deliveryError: z.string().max(500).nullable(),
	attachments: z.array(SupportAttachmentSchema),
});

// ───────────────────────── support.createDraft ─────────────────────────

export const SupportCreateDraftInputSchema = z.object({
	/**
	 * Поле-ловушка. Живой человек его не видит и не заполняет; бот заполняет
	 * всё. Заполнено — отвечаем успехом и молча ничего не делаем: сообщать
	 * боту, что он распознан, значит помогать ему подобрать обход.
	 */
	website: z.string().max(200).optional(),
});

export const SupportCreateDraftOutputSchema = z.object({
	draftId: UuidV7Schema,
	/**
	 * Секрет черновика. Идентификатора мало: `uuidv7` время-упорядочен и
	 * частично предсказуем, а под черновик выдаются presigned PUT.
	 */
	draftToken: z.string().min(32).max(128),
});

// ──────────────────────── support.attachmentUrl ────────────────────────

export const SupportAttachmentUrlInputSchema = z.object({
	draftId: UuidV7Schema,
	draftToken: z.string().min(32).max(128),
	fileName: z.string().trim().min(1).max(255),
	contentType: SupportAttachmentTypeSchema,
	sizeBytes: z.number().int().positive().max(MAX_SUPPORT_ATTACHMENTS_TOTAL_BYTES),
});

export const SupportAttachmentUrlOutputSchema = z.object({
	attachmentId: UuidV7Schema,
	uploadUrl: z.url(),
	expiresAt: IsoDateTimeSchema,
	/** Сколько байт ещё можно догрузить в это обращение — для подписи в UI. */
	remainingBytes: z.number().int().nonnegative(),
});

// ─────────────────────────── support.submit ───────────────────────────

export const SupportSubmitInputSchema = z.object({
	draftId: UuidV7Schema,
	draftToken: z.string().min(32).max(128),
	topic: SupportTopicSchema,
	/** Чем человек входил. Необязательно: он может и не помнить. */
	accountEmail: EmailSchema.nullish(),
	/** Куда с ним связаться. Обязательно — иначе обращение некуда отвечать. */
	contactEmail: EmailSchema,
	message: SupportMessageBodySchema,
	locale: LocaleSchema,
	/** Откуда пришёл: сервис в `return_to` и код с экрана ошибки входа. */
	serviceId: ServiceIdSchema.nullish(),
	errorCode: z
		.string()
		.regex(/^[a-z0-9_.-]{1,64}$/i)
		.nullish(),
	/**
	 * Согласие на обработку персональных данных — основание ст. 6(1)(a).
	 * Пара `(version, sha256)` ПОКАЗАННОГО текста, как требует
	 * rules/compliance.md §3; фронт берёт её из ответа Legal Center и не
	 * вычисляет сам. Отсутствие пары = отказ на сервере: проверка галочки на
	 * клиенте согласием не является.
	 */
	policyVersion: z.string().min(1).max(64),
	policySha256: z.string().regex(/^[a-f0-9]{64}$/, "sha256 — 64 hex-символа"),
	website: z.string().max(200).optional(),
});

export const SupportSubmitOutputSchema = z.object({
	ticketCode: SupportTicketCodeSchema,
	/**
	 * Сколько вложений реально дошло. Может быть меньше загруженного: файл,
	 * чей факт разошёлся с заявкой, отбрасывается, а обращение уходит —
	 * терять текст человека из-за файла нельзя.
	 */
	attachmentsAccepted: z.number().int().nonnegative(),
	attachmentsRejected: z.number().int().nonnegative(),
});

// ─────────────────────── Операторская часть (админка) ───────────────────────

export const SupportAdminTicketSummarySchema = z.object({
	id: UuidV7Schema,
	ticketCode: SupportTicketCodeSchema,
	topic: SupportTopicSchema,
	status: SupportTicketStatusSchema,
	contactEmail: EmailSchema,
	locale: LocaleSchema,
	/** Есть ли у обращения аккаунт в системе — ссылка на карточку пользователя. */
	userId: UuidV7Schema.nullable(),
	assigneeId: UuidV7Schema.nullable(),
	assigneeName: z.string().min(1).max(120).nullable(),
	messagesCount: z.number().int().positive(),
	attachmentsCount: z.number().int().nonnegative(),
	/** Ответ, который не удалось доставить. Видно в списке, а не только в логах. */
	hasUndeliveredReply: z.boolean(),
	createdAt: IsoDateTimeSchema,
	lastActivityAt: IsoDateTimeSchema,
});

export const SupportAdminTicketSchema = SupportAdminTicketSummarySchema.extend({
	accountEmail: EmailSchema.nullable(),
	serviceId: ServiceIdSchema.nullable(),
	errorCode: z.string().max(64).nullable(),
	/** Подпись браузера («Chrome · macOS»), не полная строка User-Agent. */
	userAgentSummary: z.string().max(120).nullable(),
	/** Усечённый IP: минимизация на записи, а не на выводе (compliance §8). */
	ipPrefix: z.string().max(64).nullable(),
	policyVersion: z.string().max(64),
	messages: z.array(SupportMessageSchema),
	closedAt: IsoDateTimeSchema.nullable(),
});

export const SupportAdminListInputSchema = z.object({
	status: SupportTicketStatusSchema.nullish(),
	topic: SupportTopicSchema.nullish(),
	assigneeId: UuidV7Schema.nullish(),
	/** Поиск по коду обращения или по любому из двух адресов. */
	query: z.string().trim().max(320).nullish(),
	/** Только те, где ответ оператора не доставлен. */
	undeliveredOnly: z.boolean().default(false),
	cursor: z.string().nullish(),
	limit: z.number().int().min(1).max(100).default(20),
});

export const SupportAdminListOutputSchema = paginated(SupportAdminTicketSummarySchema).extend({
	/** Для бейджа в навигации админки. */
	newCount: z.number().int().nonnegative(),
});

export const SupportAdminGetInputSchema = z.object({ ticketId: UuidV7Schema });

export const SupportAdminReplyInputSchema = z.object({
	ticketId: UuidV7Schema,
	body: SupportMessageBodySchema,
});

/**
 * Ответ оператора возвращает тикет целиком, а не только новое сообщение:
 * письмо уже поставлено в очередь, и карточка обязана показать оба факта —
 * и что ответ записан, и в каком он состоянии доставки.
 */
export const SupportAdminReplyOutputSchema = z.object({ ticket: SupportAdminTicketSchema });

export const SupportAdminAssignInputSchema = z.object({
	ticketId: UuidV7Schema,
	/** `null` — снять назначение. */
	assigneeId: UuidV7Schema.nullable(),
});

export const SupportAdminSetStatusInputSchema = z.object({
	ticketId: UuidV7Schema,
	/**
	 * `answered` руками не ставится: этот статус означает «человеку ушёл
	 * ответ» и появляется только вместе с отправленным письмом.
	 */
	status: z.enum(["new", "in_progress", "closed"]),
});

export const SupportAdminTicketOutputSchema = z.object({ ticket: SupportAdminTicketSchema });

export type SupportTopic = (typeof SUPPORT_TOPICS)[number];
export type SupportAttachmentType = (typeof SUPPORT_ATTACHMENT_TYPES)[number];
export type SupportTicketStatus = z.infer<typeof SupportTicketStatusSchema>;
export type SupportMessageAuthor = z.infer<typeof SupportMessageAuthorSchema>;
export type SupportMessageSource = z.infer<typeof SupportMessageSourceSchema>;
export type SupportMessage = z.infer<typeof SupportMessageSchema>;
export type SupportAttachment = z.infer<typeof SupportAttachmentSchema>;
export type SupportAdminTicket = z.infer<typeof SupportAdminTicketSchema>;
export type SupportAdminTicketSummary = z.infer<typeof SupportAdminTicketSummarySchema>;
